import { useState } from "react";
import type { CalendarEvent } from "../../shared/types.js";
import { CALENDARS_BY_ID } from "../../shared/calendars.js";
import { dayKey, relativeDayLabel, timeRange } from "../lib/eventTime.js";
import { googleCalendarUrl, singleEventIcsUrl } from "../lib/addToCalendar.js";
import { formatDescription } from "../lib/formatDescription.js";
import { BRANDING } from "../../shared/branding.js";

type Props = { event: CalendarEvent };

const TRAIL_NUMBER = /(?:#|\bRun\s*#?|\bAH3\s*#?)\s*\d+/i;

/** Imminent primary-kennel trails: expand the description by default so
 *  day-of hashers see the trail brief without an extra tap. */
function shouldDefaultExpand(event: CalendarEvent): boolean {
  if (!event.description) return false;
  if (relativeDayLabel(dayKey(event)) === null) return false;
  if (!BRANDING.calendarPrimary.sourceIds.includes(event.sourceId)) return false;
  return TRAIL_NUMBER.test(event.title);
}

export function EventCard({ event }: Props) {
  const [open, setOpen] = useState(() => shouldDefaultExpand(event));
  const source = CALENDARS_BY_ID.get(event.sourceId);
  const color = source?.color ?? "#666";

  return (
    <article className="event-card" style={{ "--source-color": color } as React.CSSProperties}>
      <div className="event-card-time">{timeRange(event)}</div>
      <div className="event-card-body">
        <h3 className="event-card-title">{event.title || "(no title)"}</h3>
        <div className="event-card-meta">
          <span
            className={`event-card-badge event-card-badge-${event.sourceKind}`}
            style={{ backgroundColor: color }}
          >
            {event.sourceName}
          </span>
          {event.location && (
            <a
              className="event-card-location"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {event.location}
            </a>
          )}
        </div>
        {event.description && (
          <button
            type="button"
            className="event-card-toggle"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
          >
            {open ? "Hide details" : "Show details"}
          </button>
        )}
        {open && event.description && (
          <div className="event-card-description">
            {formatDescription(event.description)}
          </div>
        )}
        <div className="event-card-actions">
          <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer">
            + Google Calendar
          </a>
          <a href={singleEventIcsUrl(event)} download={`${event.id}.ics`}>
            + Apple / Outlook (.ics)
          </a>
          {event.htmlLink && (
            <a href={event.htmlLink} target="_blank" rel="noopener noreferrer">
              View original
            </a>
          )}
        </div>
      </div>
    </article>
  );
}
